import { useMemo, useRef, useState } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type Column,
  type ColumnDef,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckCircle2,
  HelpCircle,
  Trash2,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SpecAdvisoryRowChip } from '@shared/lineItems';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DataTableRow, pinnedStyle } from '@/components/ui/data-table-row';
import { cn } from '@/lib/utils';
import type { FirmOfferDetailDto, SalesOrderLineItemDto } from '../types/salesOrder.types';
import {
  buildPerGradeRequested,
  computeLinePricing,
  GRADE_CODE_TO_PNS,
  markupForGrade,
  rebarVatRate,
} from '../utils/pricing';
import { isSpecValidForRematch, REMATCH_INVALID_SPEC_MESSAGE } from '../utils/lineItem';
import { lineWeightKg } from '../utils/lineWeight';
import { SalesOrderEditableSpecCell } from './SalesOrderEditableSpecCell';
import { SalesOrderLineItemInfoPopover } from './SalesOrderLineItemInfoPopover';

// 86d3fdmhb — left-pinned identifier columns: the selection checkbox, row number
// and matched SKU stay sticky during horizontal scroll so the row stays
// identifiable while reading the pricing columns. Mirrors QuotationLineItemsTable.
const PINNED_LEFT = ['_select', '_rowNum', 'matchedSku'];
const LAST_PINNED_ID = PINNED_LEFT[PINNED_LEFT.length - 1];

// Forked from QuotationLineItemsTable's column layout. Column order matches
// quotation byte-for-byte. Spec columns use SalesOrderEditableSpecCell with
// per-cell SpecContextMenu for bulk-select-by-value.

const DIAMETER_OPTIONS = [6, 8, 10, 12, 16, 20, 25, 28, 32, 36, 40, 50].map((d) => ({
  value: String(d),
  label: `${d} mm`,
}));
const LENGTH_OPTIONS = [2, 3, 4, 5, 6, 7.5, 9, 10.5, 12, 13.5, 15, 18].map((l) => ({
  value: String(l),
  label: `${l} m`,
}));
// G33 excluded — not a sellable grade (auto-promoted to G40 at extraction; no base markup ⇒
// would post at cost). The promotedFromGrade "G33↑" hint is unaffected. 86d3j5qca.
const GRADE_OPTIONS = ['40', '60', '75', '80'].map((g) => ({
  value: g,
  label: `G${g}`,
}));
// NS price levels the SO uses (matches GET /sales-orders/price-levels). Base/Average
// only — the levels FO grade lines use and the only ones with a sourceable item price.
const PRICE_LEVEL_OPTIONS = [
  { value: '1', label: 'Base Price' },
  { value: '2', label: 'Average Price' },
];
const WELDABLE_OPTIONS = [
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
];

// 86d3fdmhb — sortable column header rendered exactly like the Quotation table's:
// a ghost button (so the hover/sort affordance matches) with a trailing sort
// indicator. Keeps the SO and Quotation line-item headers visually identical.
function SortIcon({ dir }: { dir: false | 'asc' | 'desc' }) {
  if (dir === 'asc') return <ArrowUp className="ml-1 h-3.5 w-3.5" />;
  if (dir === 'desc') return <ArrowDown className="ml-1 h-3.5 w-3.5" />;
  return <ArrowUpDown className="ml-1 h-3.5 w-3.5 text-muted-foreground/50" />;
}

function SortHeader({
  label,
  column,
  align = 'left',
}: {
  // ReactNode (not just string) so a header can embed a tooltip-wrapped label
  // while keeping the sort toggle.
  label: React.ReactNode;
  column: Column<SalesOrderLineItemDto, unknown>;
  align?: 'left' | 'right';
}) {
  const alignClass = align === 'right' ? 'justify-end -mr-3' : 'justify-start -ml-3';
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={`h-8 w-full px-2 font-medium ${alignClass}`}
      onClick={column.getToggleSortingHandler()}
    >
      {label}
      <SortIcon dir={column.getIsSorted()} />
    </Button>
  );
}

interface StatusVisual {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}

const STATUS_VISUALS: Record<string, StatusVisual> = {
  MATCHED: { label: 'Matched', icon: CheckCircle2, color: 'text-success' },
  NEEDS_CLARIFICATION: { label: 'Needs info', icon: AlertTriangle, color: 'text-warning' },
  NO_MATCH: { label: 'No match', icon: XCircle, color: 'text-destructive' },
  SUMMARY_ONLY: { label: 'Summary', icon: HelpCircle, color: 'text-muted-foreground' },
  manual: { label: 'Manual', icon: HelpCircle, color: 'text-muted-foreground' },
};

function MatchStatusIcon({ status }: { status: string | null }) {
  const v: StatusVisual =
    (status ? STATUS_VISUALS[status] : undefined) ?? STATUS_VISUALS.SUMMARY_ONLY;
  const Icon = v.icon;
  return (
    <span title={v.label} className="inline-flex shrink-0 items-center">
      <Icon className={cn('h-3.5 w-3.5', v.color)} aria-label={v.label} />
    </span>
  );
}

// Amber, NON-blocking status badge for a line whose grade exceeds its FO per-grade
// guideline allocation (86d3cfnua). Per-grade is informational only — the Total
// balance is the enforced gate — so this never reads as a hard block. Carries its own
// TooltipProvider since the app has no global one.
function OverGuidelineBadge({ gradeCode }: { gradeCode: string | null }) {
  const label = gradeCode ? `G${gradeCode}` : 'This grade';
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex cursor-default">
            <Badge variant="warning" className="gap-1 px-1.5 py-0 text-[10px]">
              <AlertTriangle className="h-3 w-3" />
              Over allocation
            </Badge>
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-[240px] text-xs font-normal">
          {label} has more than the Firm Offer allocated for it. You can still post this
          order.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const PHP = new Intl.NumberFormat('en-PH', {
  style: 'currency',
  currency: 'PHP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatPhp(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—';
  return PHP.format(v);
}

// Per-line pricing, over-budget detection, and the grade-code→PNS map now live in the
// shared sales-orders/utils/pricing.ts so the table and the Order-Details Pricing panel
// use identical math (ticket 86d39hzbk). The rebar VAT rate comes from the FO customer's
// synced rate (0 for zero-rated), not a hardcoded constant.

function gradeBalanceFromFo(
  firmOffer: FirmOfferDetailDto | null,
  gradeCode: string | null,
): number | null {
  if (!firmOffer || !gradeCode) return null;
  const key = GRADE_CODE_TO_PNS[gradeCode] ?? gradeCode;
  return firmOffer.perGradeBalanceKg[key] ?? null;
}

// Right-click context menu over a spec cell — select all rows with the same
// diameter / length / grade / weldable as the right-clicked cell. Mirrors
// quotation's SpecContextMenu helper.
interface SpecContextMenuProps {
  readOnly: boolean;
  matchIds: string[];
  label: string;
  onSelectOnly: (ids: string[]) => void;
  onAdd: (ids: string[]) => void;
  onRemove: (ids: string[]) => void;
  children: React.ReactNode;
}

function SpecContextMenu({
  readOnly,
  matchIds,
  label,
  onSelectOnly,
  onAdd,
  onRemove,
  children,
}: SpecContextMenuProps) {
  if (readOnly) return <>{children}</>;
  const count = matchIds.length;
  const disabled = count === 0;
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div className="w-full">{children}</div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-56">
        <ContextMenuItem disabled={disabled} onSelect={() => onSelectOnly(matchIds)}>
          Select {label} only{count > 0 ? ` · ${count}` : ''}
        </ContextMenuItem>
        <ContextMenuItem disabled={disabled} onSelect={() => onAdd(matchIds)}>
          Add {label} to selection
        </ContextMenuItem>
        <ContextMenuItem disabled={disabled} onSelect={() => onRemove(matchIds)}>
          Remove {label} from selection
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

export type LineItemsViewMode = 'all' | 'spec' | 'pricing';

interface Props {
  lineItems: SalesOrderLineItemDto[];
  onChange: (next: SalesOrderLineItemDto[]) => void;
  readOnly?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (next: RowSelectionState) => void;
  firmOffer?: FirmOfferDetailDto | null;
  /** 86d3c2wd1: frozen pricing inputs for a read-only/submitted SO — per-grade markup
   *  (gradeCode → markupPct snapshot) + the frozen rebar VAT rate. When provided, the
   *  Pricing columns compute from these instead of the live `firmOffer`, so a posted SO
   *  renders correct as-posted prices with NO live FO re-fetch. */
  frozenMarkups?: Record<string, number>;
  frozenTaxRate?: number;
  /** FO detail still loading — pricing cells (which need the FO) render skeletons
   *  instead of computing off a null FO (86d3a1q88). */
  isLoading?: boolean;
  /** Optional per-row actions invoked from the info popover. Workspace passes
   *  these; legacy compose page leaves them undefined and the popover hides
   *  the corresponding action. */
  onRematchRow?: (clientId: string) => void;
  onRevertRow?: (clientId: string) => void;
  /** 86d3hk6pg: apply the per-row spec advisory's recommended fix to this one line
   *  (shown as an amber chip in the Status cell). Workspace passes this; undefined
   *  hides the chip. */
  onApplyRowAdvisory?: (clientId: string) => void;
  /** Tab in the panel's toolbar — drives which columns render. */
  viewMode?: LineItemsViewMode;
}

// Column ids categorised so the view-mode tab can drive visibility.
// Identifier / status / action columns are always visible. Qty is intentionally
// NOT listed here so it stays visible in every view (All / Spec / Pricing) —
// rate × qty drives the amount, so it's needed context in the Pricing view too
// (ClickUp 86d3f8234).
const SPEC_COLUMN_IDS = new Set([
  'diameter',
  'length',
  'grade',
  'weldable',
  'unit',
  'weight',
]);
const PRICING_COLUMN_IDS = new Set([
  'priceLevel',
  'rate',
  'rateWithMarkup',
  'grossRate',
  'amount',
  'grossAmount',
]);

export function SalesOrderLineItemsTable({
  lineItems,
  onChange,
  readOnly = false,
  rowSelection: controlledSelection,
  onRowSelectionChange,
  firmOffer = null,
  frozenMarkups,
  frozenTaxRate,
  isLoading = false,
  onRematchRow,
  onRevertRow,
  onApplyRowAdvisory,
  viewMode = 'all',
}: Props) {
  const [internalSelection, setInternalSelection] = useState<RowSelectionState>({});
  const rowSelection = controlledSelection ?? internalSelection;
  const setRowSelection = onRowSelectionChange ?? setInternalSelection;

  // Column visibility derived from the view-mode tab. "all" shows everything;
  // "spec" hides pricing columns; "pricing" hides spec columns.
  const columnVisibility: VisibilityState = useMemo(() => {
    const v: VisibilityState = {};
    if (viewMode === 'spec') {
      for (const id of PRICING_COLUMN_IDS) v[id] = false;
    } else if (viewMode === 'pricing') {
      for (const id of SPEC_COLUMN_IDS) v[id] = false;
    }
    // 86d3bujyr — read-only (submitted): drop the row-checkbox + per-row actions columns
    // (nothing to select / bulk-action), mirroring QuotationLineItemsTable.
    if (readOnly) {
      v._select = false;
      v._actions = false;
    }
    return v;
  }, [viewMode, readOnly]);

  function update(clientId: string, patch: Partial<SalesOrderLineItemDto>) {
    onChange(
      lineItems.map((li) =>
        li.clientId === clientId ? { ...li, ...patch, isEdited: true } : li,
      ),
    );
  }

  // 86d3cnzhr: commit a match-driving spec field (diameter / length / grade / weldable) and,
  // when it actually changed and the line's spec is now complete, auto re-match so the matched
  // item always reflects the current spec. Without this, editing a spec after the initial match
  // leaves a stale item whose grade can disagree with the line — and NetSuite re-prices off the
  // ITEM's grade, so DOP and NetSuite diverge on price. The matcher is exact-grade, so the
  // re-match lands on the correct-grade item or NO_MATCH (the readiness gate then flags it).
  // Non-match fields (quantity / unit / price level) keep using `update` — no re-match needed.
  function commitSpec(clientId: string, patch: Partial<SalesOrderLineItemDto>) {
    const before = lineItems.find((li) => li.clientId === clientId);
    update(clientId, patch);
    const merged = { ...before, ...patch };
    const matchFieldChanged =
      before == null ||
      (['diameterMm', 'lengthM', 'gradeCode', 'isWeldable'] as const).some(
        (k) => before[k] !== merged[k],
      );
    // 86d3dxnd7: gate on server-validator validity (valid grade + positive
    // diameter/length), not mere presence — else editing a spec on a line with a
    // non-canonical grade fires an auto-rematch that 400s.
    if (matchFieldChanged && isSpecValidForRematch(merged)) {
      rowActionsRef.current.onRematchRow?.(clientId);
    }
  }

  function remove(clientId: string) {
    onChange(lineItems.filter((li) => li.clientId !== clientId));
  }

  // SpecContextMenu helpers — set / add / remove ids to the row selection.
  function setOnlyGroup(ids: string[]) {
    const next: RowSelectionState = {};
    ids.forEach((id) => (next[id] = true));
    setRowSelection(next);
  }
  function addToSelection(ids: string[]) {
    const next: RowSelectionState = { ...rowSelection };
    ids.forEach((id) => (next[id] = true));
    setRowSelection(next);
  }
  function removeFromSelection(ids: string[]) {
    const next: RowSelectionState = { ...rowSelection };
    ids.forEach((id) => {
      delete next[id];
    });
    setRowSelection(next);
  }

  // Row-action callbacks via a ref so the `columns` memo does NOT depend on them.
  // The parent's rematch/revert handlers capture `activeDraft`, which is a new object
  // after every autosave — depending on them rebuilt the columns mid-interaction and
  // tore down an open cell popover (e.g. the Length select) on each autosave (86d3ckf8h).
  // The cells read the latest handlers from this ref at click time; the memo now
  // recomputes only on real data changes (lineItems, etc.).
  const rowActionsRef = useRef({ onRematchRow, onRevertRow, onApplyRowAdvisory });
  rowActionsRef.current = { onRematchRow, onRevertRow, onApplyRowAdvisory };

  const perGradeRequested = useMemo(
    () => buildPerGradeRequested(lineItems),
    [lineItems],
  );

  const overBudgetClientIds = useMemo(() => {
    if (!firmOffer) return new Set<string>();
    const overGrades = new Set<string>();
    for (const [grade, requested] of perGradeRequested.entries()) {
      const available = gradeBalanceFromFo(firmOffer, grade);
      if (available != null && requested > available) overGrades.add(grade);
    }
    if (overGrades.size === 0) return new Set<string>();
    const ids = new Set<string>();
    for (const li of lineItems) {
      if (li.gradeCode && overGrades.has(li.gradeCode)) ids.add(li.clientId);
    }
    return ids;
  }, [perGradeRequested, firmOffer, lineItems]);

  // Pre-compute value→ids indexes once per render so cell-level context menus
  // can resolve their match groups O(1). Mirrors quotation's lineItemDerivations.
  const derived = useMemo(() => {
    const byDiameter = new Map<number | null, string[]>();
    const byLength = new Map<number | null, string[]>();
    const byGrade = new Map<string | null, string[]>();
    const byWeldable = new Map<boolean, string[]>();
    const push = <K,>(m: Map<K, string[]>, k: K, id: string) => {
      const arr = m.get(k);
      if (arr) arr.push(id);
      else m.set(k, [id]);
    };
    for (const li of lineItems) {
      push(byDiameter, li.diameterMm ?? null, li.clientId);
      push(byLength, li.lengthM ?? null, li.clientId);
      push(byGrade, li.gradeCode ?? null, li.clientId);
      push(byWeldable, li.isWeldable, li.clientId);
    }
    return { byDiameter, byLength, byGrade, byWeldable };
  }, [lineItems]);

  // Uniform rebar VAT for the gross-amount column. 86d3c2wd1: prefer the frozen rate
  // (read-only/submitted — no live FO); else the live FO customer's synced rate.
  const rebarTax = frozenTaxRate ?? rebarVatRate(firmOffer);

  // Per-line markup: frozen snapshot (read-only) vs live FO. Both feed computeLinePricing
  // identically — the frozen value is the same markupPct captured at draft-save.
  const markupOf = (gradeCode: string | null): number =>
    frozenMarkups
      ? gradeCode != null
        ? frozenMarkups[gradeCode] ?? 0
        : 0
      : markupForGrade(gradeCode, firmOffer);

  const columnHelper = createColumnHelper<SalesOrderLineItemDto>();

  const columns = useMemo<ColumnDef<SalesOrderLineItemDto, unknown>[]>(
    () => [
      // 1. Select
      {
        id: '_select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllRowsSelected()
                ? true
                : table.getIsSomeRowsSelected()
                  ? 'indeterminate'
                  : false
            }
            onCheckedChange={(v) => table.toggleAllRowsSelected(!!v)}
            aria-label="Select all rows"
            disabled={readOnly}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(v) => row.toggleSelected(!!v)}
            aria-label="Select row"
            disabled={readOnly}
          />
        ),
        size: 32,
        enableSorting: false,
      },
      // 2. #
      {
        id: '_rowNum',
        header: () => <span className="text-muted-foreground">#</span>,
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground tabular-nums">{row.index + 1}</span>
        ),
        size: 40,
        enableSorting: false,
      },
      // 3. Matched SKU / Description
      columnHelper.accessor(
        (li) => li.matchedSalesDescription || li.rawDescription || '',
        {
          id: 'matchedSku',
          header: ({ column }) => <SortHeader label="Matched SKU" column={column} />,
          cell: ({ row }) => {
            const li = row.original;
            return (
              <div className="min-w-0">
                <div className="truncate font-medium">
                  {li.matchedSalesDescription || (
                    <span className="text-muted-foreground italic">
                      {li.rawDescription || '—'}
                    </span>
                  )}
                </div>
                {li.matchedSalesDescription &&
                  li.rawDescription &&
                  li.rawDescription !== li.matchedSalesDescription && (
                    <div className="truncate text-[11px] text-muted-foreground">
                      from “{li.rawDescription}”
                    </div>
                  )}
              </div>
            );
          },
          size: 240,
        },
      ),
      // 4. Diameter — EditableSpecCell + SpecContextMenu
      columnHelper.accessor('diameterMm', {
        id: 'diameter',
        header: ({ column }) => <SortHeader label="Diameter" column={column} />,
        cell: ({ row }) => {
          const li = row.original;
          return (
            <SpecContextMenu
              readOnly={readOnly}
              matchIds={derived.byDiameter.get(li.diameterMm ?? null) ?? []}
              label={li.diameterMm != null ? `${li.diameterMm}mm` : '— (no diameter)'}
              onSelectOnly={setOnlyGroup}
              onAdd={addToSelection}
              onRemove={removeFromSelection}
            >
              <SalesOrderEditableSpecCell
                kind="select"
                value={li.diameterMm != null ? String(li.diameterMm) : null}
                options={DIAMETER_OPTIONS}
                onCommit={(v) =>
                  commitSpec(li.clientId, { diameterMm: v == null ? null : Number(v) })
                }
                disabled={readOnly}
                ariaLabel="Diameter"
              />
            </SpecContextMenu>
          );
        },
        size: 110,
      }),
      // 5. Length
      columnHelper.accessor('lengthM', {
        id: 'length',
        header: ({ column }) => <SortHeader label="Length" column={column} />,
        cell: ({ row }) => {
          const li = row.original;
          return (
            <SpecContextMenu
              readOnly={readOnly}
              matchIds={derived.byLength.get(li.lengthM ?? null) ?? []}
              label={li.lengthM != null ? `${li.lengthM}m` : '— (no length)'}
              onSelectOnly={setOnlyGroup}
              onAdd={addToSelection}
              onRemove={removeFromSelection}
            >
              <SalesOrderEditableSpecCell
                kind="select"
                value={li.lengthM != null ? String(li.lengthM) : null}
                options={LENGTH_OPTIONS}
                onCommit={(v) =>
                  commitSpec(li.clientId, { lengthM: v == null ? null : Number(v) })
                }
                disabled={readOnly}
                ariaLabel="Length"
              />
            </SpecContextMenu>
          );
        },
        size: 110,
      }),
      // 6. Grade
      columnHelper.accessor('gradeCode', {
        id: 'grade',
        header: ({ column }) => <SortHeader label="Grade" column={column} />,
        cell: ({ row }) => {
          const li = row.original;
          return (
            <SpecContextMenu
              readOnly={readOnly}
              matchIds={derived.byGrade.get(li.gradeCode ?? null) ?? []}
              label={li.gradeCode != null ? `G${li.gradeCode}` : '— (no grade)'}
              onSelectOnly={setOnlyGroup}
              onAdd={addToSelection}
              onRemove={removeFromSelection}
            >
              <div className="flex items-center gap-1">
                <SalesOrderEditableSpecCell
                  kind="select"
                  value={li.gradeCode}
                  options={GRADE_OPTIONS}
                  onCommit={(v) => commitSpec(li.clientId, { gradeCode: v })}
                  disabled={readOnly}
                  ariaLabel="Grade"
                />
                {li.promotedFromGrade != null && (
                  <span
                    className="rounded bg-warning/10 px-1 text-[10px] font-semibold text-warning"
                    title={`Promoted from G${li.promotedFromGrade} per CSC grade policy.`}
                  >
                    G{li.promotedFromGrade}↑
                  </span>
                )}
              </div>
            </SpecContextMenu>
          );
        },
        size: 110,
      }),
      // 7. Weldable
      columnHelper.accessor('isWeldable', {
        id: 'weldable',
        // Compact "W" header with a hover hint — the full word doesn't fit the
        // dense column and the Y/N cell is self-explanatory once known (86d3f8234).
        header: () => (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-default underline decoration-dotted decoration-muted-foreground/50 underline-offset-2">
                  W
                </span>
              </TooltipTrigger>
              <TooltipContent className="text-xs font-normal">Weldable</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ),
        cell: ({ row }) => {
          const li = row.original;
          return (
            <SpecContextMenu
              readOnly={readOnly}
              matchIds={derived.byWeldable.get(li.isWeldable) ?? []}
              label={li.isWeldable ? 'Weldable' : 'Non-weldable'}
              onSelectOnly={setOnlyGroup}
              onAdd={addToSelection}
              onRemove={removeFromSelection}
            >
              <SalesOrderEditableSpecCell
                kind="select"
                value={li.isWeldable ? 'Y' : 'N'}
                options={WELDABLE_OPTIONS}
                onCommit={(v) => commitSpec(li.clientId, { isWeldable: v === 'Y' })}
                disabled={readOnly}
                ariaLabel="Weldable"
                placeholder="N"
              />
            </SpecContextMenu>
          );
        },
        size: 90,
      }),
      // 8. Qty — also re-derives weightKg proportionally so the per-row /
      //    per-grade / running totals stay accurate when qty is edited.
      //    Falls back to leaving weightKg alone when we can't derive a
      //    per-piece factor (e.g. previous qty was null/0).
      columnHelper.accessor('quantity', {
        id: 'qty',
        header: ({ column }) => <SortHeader label="Qty" column={column} align="right" />,
        cell: ({ row }) => {
          const li = row.original;
          return (
            <SalesOrderEditableSpecCell
              kind="number"
              value={li.quantity}
              onCommit={(v) => {
                const patch: Partial<SalesOrderLineItemDto> = { quantity: v };
                // Re-derive line weight locally — no re-match round-trip (86d3dxnd7).
                // Prefer the matched SKU's per-piece weight so a manually-added line
                // matched before a qty existed still gets weight the moment a qty is
                // typed. Fall back to the prior weight÷qty ratio for legacy lines
                // saved before weightPerPcKg existed (unit is always 'pcs').
                if (v != null && v > 0) {
                  if (li.weightPerPcKg != null) {
                    patch.weightKg = li.weightPerPcKg * v;
                  } else if (li.quantity != null && li.quantity > 0 && li.weightKg != null) {
                    patch.weightKg = (li.weightKg / li.quantity) * v;
                  }
                }
                update(li.clientId, patch);
              }}
              min={1}
              step={1}
              disabled={readOnly}
              ariaLabel="Quantity"
            />
          );
        },
        size: 100,
        // Lock a minimum width on the qty column. Its cell is a `w-full` <input> with a
        // tiny min-content, so under table-layout:auto the browser squeezes this column to
        // feed the long SKU column — the inline `width: size` is only a suggestion, with no
        // floor — and the value clips. min-w on the body cell forces the whole shared column
        // to stay ≥ its intended width (86d3f8234 follow-up: width, separate from the spinner fix).
        meta: { cellClassName: 'min-w-[100px]' },
      }),
      // 9. Unit — fixed to "pcs"; the only unit, read-only (86d3dxnd7).
      columnHelper.accessor('unit', {
        id: 'unit',
        header: 'Unit',
        cell: () => <div className="px-1.5 py-1 text-sm text-foreground">pcs</div>,
        size: 80,
      }),
      // 10. Weight (kg) — pieces × kg per piece (lineWeightKg, 14ykpqtv3qw), the weight the
      //     submit check uses. The stored weightKg was the quantity for a kg-labelled line.
      columnHelper.accessor((li) => lineWeightKg(li), {
        id: 'weight',
        // Header trimmed to "Weight" — the per-row "kg" suffix carries the unit (86d3f8234).
        header: ({ column }) => <SortHeader label="Weight" column={column} align="right" />,
        cell: ({ row }) => {
          const v = lineWeightKg(row.original);
          return (
            <div className="text-right font-mono text-xs tabular-nums">
              {v != null ? (
                <>
                  {v.toLocaleString('en-PH', { maximumFractionDigits: 2 })}
                  <span className="ml-1 text-muted-foreground">kg</span>
                </>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </div>
          );
        },
        size: 112,
      }),
      // 11. Price Level — editable; defaults from the FO grade line, drives the rate.
      columnHelper.accessor('priceLevel', {
        id: 'priceLevel',
        header: ({ column }) => <SortHeader label="Price Level" column={column} />,
        cell: ({ row }) => {
          const li = row.original;
          return (
            <SalesOrderEditableSpecCell
              kind="select"
              value={li.priceLevel}
              options={PRICE_LEVEL_OPTIONS}
              onCommit={(v) => update(li.clientId, { priceLevel: v })}
              disabled={readOnly}
              ariaLabel="Price level"
              placeholder="—"
            />
          );
        },
        size: 132,
      }),
      // 12. Rate (base / customrate — item price at the chosen level)
      {
        id: 'rate',
        header: ({ column }) => <SortHeader label="Rate" column={column} align="right" />,
        cell: ({ row }) => {
          if (isLoading) return <Skeleton className="ml-auto h-4 w-16" />;
          const p = computeLinePricing(row.original, markupOf(row.original.gradeCode), rebarTax);
          return <div className="text-right tabular-nums">{formatPhp(p.rate)}</div>;
        },
        size: 116,
      },
      // 13. Rate w/ Markup (base × (1 + FO grade markup) — the posted selling rate)
      {
        id: 'rateWithMarkup',
        header: ({ column }) => (
          <SortHeader label="Rate w/ Markup" column={column} align="right" />
        ),
        cell: ({ row }) => {
          if (isLoading) return <Skeleton className="ml-auto h-4 w-16" />;
          const p = computeLinePricing(row.original, markupOf(row.original.gradeCode), rebarTax);
          return <div className="text-right tabular-nums">{formatPhp(p.rateWithMarkup)}</div>;
        },
        size: 144,
      },
      // 14. Price/pc (VAT incl.) — rateWithMarkup × (1 + VAT), rounded to the centavo.
      // This is the per-piece unit the customer PO is built from (round(unit,2) × qty),
      // so it reconciles the Gross Amount column with the PO line totals (86d3fu6tc).
      {
        id: 'grossRate',
        header: ({ column }) => (
          <SortHeader label="Price/pc (VAT incl.)" column={column} align="right" />
        ),
        cell: ({ row }) => {
          if (isLoading) return <Skeleton className="ml-auto h-4 w-16" />;
          const p = computeLinePricing(row.original, markupOf(row.original.gradeCode), rebarTax);
          return <div className="text-right tabular-nums">{formatPhp(p.grossRate)}</div>;
        },
        size: 152,
      },
      // 15. Amount
      {
        id: 'amount',
        header: ({ column }) => <SortHeader label="Amount" column={column} align="right" />,
        cell: ({ row }) => {
          if (isLoading) return <Skeleton className="ml-auto h-4 w-16" />;
          const p = computeLinePricing(row.original, markupOf(row.original.gradeCode), rebarTax);
          return <div className="text-right tabular-nums">{formatPhp(p.amount)}</div>;
        },
        size: 128,
      },
      // 16. Gross Amount
      {
        id: 'grossAmount',
        header: ({ column }) => (
          <SortHeader label="Gross Amount" column={column} align="right" />
        ),
        cell: ({ row }) => {
          if (isLoading) return <Skeleton className="ml-auto h-4 w-16" />;
          const p = computeLinePricing(row.original, markupOf(row.original.gradeCode), rebarTax);
          return <div className="text-right tabular-nums">{formatPhp(p.grossAmount)}</div>;
        },
        size: 140,
      },
      // 17. Status — match status + (when the line's grade is over its FO guideline)
      // an amber, non-blocking "Over guideline" badge with an explanatory tooltip.
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const li = row.original;
          const overGuideline = overBudgetClientIds.has(li.clientId);
          return (
            <div className="flex items-center gap-1.5">
              <MatchStatusIcon status={li.matchStatus} />
              {overGuideline && <OverGuidelineBadge gradeCode={li.gradeCode} />}
              {!readOnly && (
                <SpecAdvisoryRowChip
                  line={li}
                  onApply={() => rowActionsRef.current.onApplyRowAdvisory?.(li.clientId)}
                />
              )}
            </div>
          );
        },
        size: 150,
        enableSorting: false,
      },
      // 18. Info popover + Actions (combined to keep the column count tight)
      {
        id: '_actions',
        header: '',
        cell: ({ row }) => {
          const li = row.original;
          return (
            <div className="flex items-center justify-end gap-0.5">
              <SalesOrderLineItemInfoPopover
                item={li}
                onRematch={
                  !readOnly && rowActionsRef.current.onRematchRow
                    ? () => rowActionsRef.current.onRematchRow!(li.clientId)
                    : undefined
                }
                rematchDisabledReason={
                  isSpecValidForRematch(li) ? undefined : REMATCH_INVALID_SPEC_MESSAGE
                }
                onRevert={
                  !readOnly && rowActionsRef.current.onRevertRow
                    ? () => rowActionsRef.current.onRevertRow!(li.clientId)
                    : undefined
                }
                onDelete={!readOnly ? () => remove(li.clientId) : undefined}
              />
              {!readOnly && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => remove(li.clientId)}
                  title="Delete line"
                  aria-label="Delete line"
                >
                  <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                </Button>
              )}
            </div>
          );
        },
        size: 80,
        enableSorting: false,
      },
    ],
    // Recompute on real data changes only. Row-action callbacks (onRematchRow/onRevertRow)
    // are intentionally NOT deps — they go through rowActionsRef so an autosave (which makes
    // activeDraft a new object, hence new callbacks) doesn't rebuild columns and close an
    // open cell popover (86d3ckf8h).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lineItems, readOnly, overBudgetClientIds, derived, rowSelection, firmOffer, frozenMarkups, rebarTax, isLoading],
  );

  const table = useReactTable({
    data: lineItems,
    columns,
    state: { rowSelection, columnVisibility },
    getRowId: (row) => row.clientId,
    onRowSelectionChange: (updater) => {
      const next = typeof updater === 'function' ? updater(rowSelection) : updater;
      setRowSelection(next);
    },
    enableRowSelection: !readOnly,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: { sorting: [] as SortingState, columnPinning: { left: PINNED_LEFT } },
  });

  return (
    <div className="overflow-hidden rounded-md border bg-card">
      {/* One bounded scroll box owns BOTH axes (max-h + the shadcn container's
          overflow-auto) so the sticky <thead> pins within it while the rows
          scroll — and horizontal scroll is preserved. The section's toolbar /
          quick-select / bulk-action controls sit above this box and stay put;
          weight + freight totals sit below (86d3f8f9n). */}
      <Table containerClassName="max-h-[60vh]">
        {/* The frosted (backdrop-blur) treatment lives on the NON-pinned header
            cells, not the <thead>: a backdrop-filter ancestor suppresses the border
            + shadow on the sticky pinned cells, which hid the matched-SKU divider.
            Keeping the blur off the pinned cells lets that divider render like the
            rows while the rest of the header stays frosted (86d3fdmhb). */}
        <TableHeader className="sticky top-0 z-20">
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((header) => {
                  const meta = header.column.columnDef.meta as
                    | { headerClassName?: string }
                    | undefined;
                  const isPinned = header.column.getIsPinned() === 'left';
                  const isLast = isPinned && header.column.id === LAST_PINNED_ID;
                  return (
                    <TableHead
                      key={header.id}
                      style={{
                        width: header.column.columnDef.size,
                        minWidth: header.column.columnDef.size,
                        ...pinnedStyle(header.column, 20),
                      }}
                      className={cn(
                        'h-9 whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide text-muted-foreground',
                        // Pinned cells are opaque (no backdrop-filter ancestor → their
                        // sticky matched-SKU divider renders); non-pinned cells carry the
                        // frosted blur so the rest of the header still frosts scrolled rows.
                        isPinned
                          ? 'bg-background'
                          : 'bg-muted/30 backdrop-blur supports-[backdrop-filter]:bg-muted/30',
                        // Header→body separator is an inset box-shadow, NOT a border-b: a
                        // real border is eaten here both by the collapsed <tr> border and
                        // by the non-pinned cells' own backdrop-blur layer (the line showed
                        // only under the unblurred pinned SKU cell). An inset shadow paints
                        // on the cell's own layer, immune to both, so it renders uniformly.
                        // isLast also keeps the right divider shadow (86d3fdmhb).
                        isLast
                          ? 'border-r shadow-[inset_0_-1px_0_0_hsl(var(--border)),2px_0_4px_-2px_rgba(0,0,0,0.08)]'
                          : 'shadow-[inset_0_-1px_0_0_hsl(var(--border))]',
                        meta?.headerClassName,
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <DataTableRow key={row.id} row={row} lastPinnedLeftId={LAST_PINNED_ID} />
            ))}
          </TableBody>
        </Table>
    </div>
  );
}
